import {
  Box,
  DropdownItem,
  DropdownList,
  Flex,
  Icon,
  IconName,
  IndicatorBadge,
  IndicatorBadgeVariants,
  MixpanelEvent,
  MixpanelEventProperty,
  Popover,
  useDisclosure,
  useMixpanel,
  usePopoverState,
  usePortal,
} from '@talos/kyoko';
import type { IDockviewDefaultTabProps } from 'dockview';
import { useCallback } from 'react';
import { v1 as uuid } from 'uuid';
import { useDisplaySettings } from '../../../providers/AppConfigProvider';
import { useDisplayMode } from '../../../utils/getDisplayMode';
import { PopoutStyleProvider } from '../../PopoutWindow/PopoutStyleProvider';
import { useDockviewPopoutWindow } from '../hooks/useDockviewPopoutWindow';
import { useLayoutContext } from '../providers/LayoutContextProvider';
import { TabWrapper } from '../styles';
import { MAIN_GROUP_ID, POPOUT_URL } from '../tokens';
import { RemoveTabDialog } from './RemoveTabDialog';
import { RenameTabDialog } from './RenameTabDialog';

export const DefaultTabComponent = (props: IDockviewDefaultTabProps & { canDuplicate?: boolean }) => {
  const notificationsCount = 0; // TODO: Get notifications count
  const mixpanel = useMixpanel();
  const { enablePopoutWindows, onPopoutGroup, disallowLastCloseMode, hiddenPanelIds } = useLayoutContext();
  const { shouldConfirmCloseTab } = useDisplaySettings();
  const popoutWindow = useDockviewPopoutWindow(props.api);
  const isPopout = popoutWindow != null;
  const displayMode = useDisplayMode();
  const canPopout = displayMode !== 'fullscreen' && enablePopoutWindows && !isPopout;

  const portalElement = popoutWindow?.document.body ?? document.body;

  const renameTabDialog = useDisclosure();
  const removeTabDialog = useDisclosure();

  const popover = usePopoverState({
    trigger: '',
    noPaddingAndBorder: true,
    closeOnClickOutside: true,
    usePortal: true,
    placement: 'bottom-start',
    popoverDocument: popoutWindow?.document,
  });

  let disableClose = props.params.readOnly || props.hideClose;
  if (!disableClose) {
    const visibleGroups = props.containerApi.groups.filter(g => g.isVisible);
    const totalVisiblePanels = visibleGroups.reduce(
      (count, g) => count + g.panels.filter(p => !hiddenPanelIds.has(p.id)).length,
      0
    );
    disableClose = totalVisiblePanels <= 1;
  }
  if (!disableClose && disallowLastCloseMode !== 'lastTabInContainer') {
    const visiblePanelsInGroup = props.api.group.panels.filter(p => !hiddenPanelIds.has(p.id));
    disableClose = props.api.group.id === MAIN_GROUP_ID && visiblePanelsInGroup.length === 1;
  }

  const onConfirmRemove = useCallback(() => {
    props.api.close();
    mixpanel.track(MixpanelEvent.CloseTab, {
      [MixpanelEventProperty.TabType]: props.api.component,
    });
  }, [props.api, mixpanel]);

  const onClose = useCallback(() => {
    if (disableClose) {
      return;
    }

    popover.close();
    if (props.params.readOnly || props.hideClose) {
      return;
    }
    if (!shouldConfirmCloseTab) {
      onConfirmRemove();
      return;
    }
    removeTabDialog.open();
  }, [props, popover, disableClose, shouldConfirmCloseTab, onConfirmRemove, removeTabDialog]);

  const onConfirmRename = useCallback(
    (title: string) => {
      if (title) {
        props.api.setTitle(title);
        props.api.updateParameters({ customTitle: true });
        mixpanel.track(MixpanelEvent.RenameTab, {
          [MixpanelEventProperty.TabType]: props.api.component,
        });
      }
    },
    [props.api, mixpanel]
  );

  const openRenameDialog = useCallback(() => {
    popover.close();
    renameTabDialog.open();
  }, [popover, renameTabDialog]);

  const duplicateTab = useCallback(() => {
    props.containerApi.addPanel({
      component: props.api.component,
      title: `${props.api.title} (copy)`,
      params: props.params,
      id: uuid(),
      position: {
        referenceGroup: props.api.group,
      },
    });
    mixpanel.track(MixpanelEvent.DuplicateTab, {
      [MixpanelEventProperty.TabType]: props.api.component,
    });
    popover.close();
  }, [props, mixpanel, popover]);

  const handleOpenMenu = useCallback(
    (e: React.MouseEvent) => {
      if (e.metaKey) {
        return;
      }
      mixpanel.track(MixpanelEvent.OpenTabMenu, {
        [MixpanelEventProperty.TabType]: props.api.component,
      });
      popover.open();
      e.preventDefault();
    },
    [popover, mixpanel, props.api.component]
  );

  const handlePopout = useCallback(() => {
    popover.close();
    const panelInstance = props.api.group.panels.find(p => p.id === props.api.id);
    if (panelInstance == null) {
      return;
    }
    props.containerApi.addPopoutGroup(panelInstance, {
      popoutUrl: POPOUT_URL,
      onDidOpen: ({ window: popoutWindow, id }) => {
        onPopoutGroup({
          groupId: props.api.group.api.id,
          popoutWindow,
          windowId: id,
          popoutTitle: props.api.title,
        });
        mixpanel.track(MixpanelEvent.PopoutLayoutPanel, {
          [MixpanelEventProperty.PanelType]: props.api.component,
        });
      },
      onWillClose: ({ id }) => {
        onPopoutGroup({ groupId: props.api.group.api.id, popoutWindow: undefined, windowId: id });
        mixpanel.track(MixpanelEvent.CloseLayoutPopout, {
          [MixpanelEventProperty.PanelType]: props.api.component,
        });
      },
    });
  }, [props, onPopoutGroup, mixpanel, popover]);

  const tabMenuPortalId = `tabMenu_${props.api.id}`;
  const { setPortalRef } = usePortal(tabMenuPortalId);

  // If the panel is hidden, don't render the tab
  if (hiddenPanelIds.has(props.api.id)) {
    return null;
  }

  return (
    /* One of four Dockview rendering outlets. <Panel> is in charge of including the global styles, so we don't bother. */
    <PopoutStyleProvider includeGlobalStyles={false} popoutWindow={popoutWindow}>
      <>
        <Popover {...popover} portalElement={portalElement} targetStyle={POPOVER_TARGET_STYLE}>
          <TabWrapper
            data-testid={`${props.api.id}-layout-tab`}
            onContextMenu={handleOpenMenu}
            onDoubleClick={openRenameDialog}
            role="tab"
            onAuxClick={e => {
              if (e.button === 1) {
                onClose();
              }
            }}
          >
            <Box>{props.api.title}</Box>
            {notificationsCount && notificationsCount > 0 ? (
              <IndicatorBadge children={notificationsCount} variant={IndicatorBadgeVariants.Notification} />
            ) : null}
            {disableClose ? null : (
              <Box className="dv-default-tab-action" data-testid="close-tab-button">
                <Icon icon={IconName.Close} onClick={onClose} size={12} />
              </Box>
            )}
          </TabWrapper>
          <DropdownList data-testid="tab-menu">
            <DropdownItem
              startIcon={IconName.PencilField}
              onClick={() => {
                popover.close();
                openRenameDialog();
              }}
            >
              Rename
            </DropdownItem>
            {(props.canDuplicate ?? true) && (
              <DropdownItem
                startIcon={IconName.Duplicate}
                onClick={() => {
                  popover.close();
                  duplicateTab();
                }}
              >
                Duplicate
              </DropdownItem>
            )}
            {canPopout && (
              <DropdownItem
                startIcon={IconName.ExternalLink}
                onClick={() => {
                  popover.close();
                  handlePopout();
                }}
              >
                Popout
              </DropdownItem>
            )}
            {/* Portal target for adding custom tab menu items based on the panel content */}
            <Flex flexDirection="column" ref={setPortalRef} />
            {disableClose ? null : (
              <DropdownItem
                startIcon={IconName.Trash}
                onClick={() => {
                  popover.close();
                  onClose();
                }}
              >
                Remove
              </DropdownItem>
            )}
          </DropdownList>
        </Popover>
        <RenameTabDialog
          key={`rename_${renameTabDialog.isOpen.toString()}`}
          initialTitle={props.api.title}
          onConfirm={onConfirmRename}
          portalElement={portalElement}
          {...renameTabDialog}
        />
        <RemoveTabDialog
          key={`remove_${removeTabDialog.isOpen.toString()}`}
          panel={props}
          onConfirm={onConfirmRemove}
          portalElement={portalElement}
          {...removeTabDialog}
        />
      </>
    </PopoutStyleProvider>
  );
};

const POPOVER_TARGET_STYLE = {
  height: '100%',
};
